
import React, { useState } from 'react';
import { X, Users, Calendar, Zap, BarChart2, Trophy, ChevronRight, ChevronLeft, Sparkles } from 'lucide-react';

interface WelcomeTourModalProps {
  onComplete: () => void;
  onSkip: () => void;
}

const slides = [
  {
    icon: Trophy,
    color: 'bg-blue-600',
    bgLight: 'bg-blue-50',
    textColor: 'text-blue-600',
    title: 'Welcome to SideoutIQ',
    subtitle: 'Command the Court',
    description: 'Your complete volleyball management platform — roster, schedule, live game tracking, and stats in one place.',
    visual: null,
  },
  {
    icon: Users,
    color: 'bg-violet-600',
    bgLight: 'bg-violet-50',
    textColor: 'text-violet-600',
    title: 'Team Management',
    subtitle: 'Build your roster',
    description: 'Add players with positions and jersey numbers, build lineup rotations, and set up your depth chart. Designate liberos and create multiple lineup options.',
    visual: (
      <div className="flex gap-2 flex-wrap justify-center">
        {['#1 Setter', '#3 OH', '#7 Opp', '#11 Libero', '#5 MB'].map((p) => (
          <span key={p} className="px-2 py-1 bg-violet-100 text-violet-700 rounded-lg text-xs font-medium">{p}</span>
        ))}
      </div>
    ),
  },
  {
    icon: Calendar,
    color: 'bg-emerald-600',
    bgLight: 'bg-emerald-50',
    textColor: 'text-emerald-600',
    title: 'Schedule',
    subtitle: 'Track every event',
    description: 'Add games, practices, and tournaments. See what\'s coming up, log results after games, and jump straight into live game tracking from any event.',
    visual: (
      <div className="space-y-1.5 w-full max-w-[200px]">
        {[
          { label: 'Tue, Feb 14', type: 'Game', color: 'bg-blue-100 text-blue-700' },
          { label: 'Thu, Feb 16', type: 'Practice', color: 'bg-emerald-100 text-emerald-700' },
          { label: 'Sat, Feb 21', type: 'Game', color: 'bg-blue-100 text-blue-700' },
        ].map((e) => (
          <div key={e.label} className="flex items-center justify-between bg-white rounded-lg px-3 py-1.5 border border-slate-100 text-xs">
            <span className="text-slate-600">{e.label}</span>
            <span className={`px-2 py-0.5 rounded-full font-medium ${e.color}`}>{e.type}</span>
          </div>
        ))}
      </div>
    ),
  },
  {
    icon: Zap,
    color: 'bg-amber-500',
    bgLight: 'bg-amber-50',
    textColor: 'text-amber-600',
    title: 'Game Time',
    subtitle: 'Live game tracking',
    description: 'Track scores, rotations, and substitutions in real time. Tap to log kills, digs, blocks, aces, and errors for every player while you coach.',
    visual: (
      <div className="w-full max-w-[220px] bg-slate-900 rounded-xl p-3 text-white">
        <div className="flex justify-between items-center mb-2">
          <div className="text-center">
            <div className="text-xs text-slate-400">Eagles</div>
            <div className="text-2xl font-bold">14</div>
          </div>
          <div className="text-xs text-slate-500 font-medium">Set 2</div>
          <div className="text-center">
            <div className="text-xs text-slate-400">Cougars</div>
            <div className="text-2xl font-bold">11</div>
          </div>
        </div>
        <div className="flex gap-1.5">
          <button className="flex-1 bg-blue-600 text-white text-xs py-1.5 rounded-lg font-medium">Our Point</button>
          <button className="flex-1 bg-slate-700 text-white text-xs py-1.5 rounded-lg font-medium">Their Point</button>
        </div>
      </div>
    ),
  },
  {
    icon: BarChart2,
    color: 'bg-rose-600',
    bgLight: 'bg-rose-50',
    textColor: 'text-rose-600',
    title: 'Stats & Analytics',
    subtitle: 'Insights that matter',
    description: 'See hitting efficiency, serve percentage, reception average, and individual player breakdowns. Compare game-by-game trends and spot areas to improve.',
    visual: (
      <div className="w-full max-w-[220px] space-y-1.5">
        {[
          { label: 'Hit Efficiency', value: '.312', bar: 75 },
          { label: 'Serve %', value: '87%', bar: 87 },
          { label: 'Pass Avg', value: '2.4', bar: 60 },
        ].map((s) => (
          <div key={s.label} className="flex items-center gap-2">
            <span className="text-xs text-slate-500 w-24 shrink-0">{s.label}</span>
            <div className="flex-1 bg-slate-100 rounded-full h-2">
              <div className="bg-rose-500 h-2 rounded-full" style={{ width: `${s.bar}%` }} />
            </div>
            <span className="text-xs font-bold text-slate-700 w-8 text-right">{s.value}</span>
          </div>
        ))}
      </div>
    ),
  },
  {
    icon: Sparkles,
    color: 'bg-blue-600',
    bgLight: 'bg-blue-50',
    textColor: 'text-blue-600',
    title: "You're ready!",
    subtitle: "Let's get started",
    description: 'Start by adding your team, then schedule your first game. You can revisit this guide anytime from Settings → Restart Tour.',
    visual: (
      <div className="flex gap-3">
        <div className="flex flex-col items-center gap-1">
          <div className="w-8 h-8 bg-blue-600 rounded-full flex items-center justify-center text-white text-sm font-bold">1</div>
          <div className="text-xs text-slate-600 text-center">Add<br/>Players</div>
        </div>
        <div className="flex items-center pb-5"><ChevronRight className="w-4 h-4 text-slate-300" /></div>
        <div className="flex flex-col items-center gap-1">
          <div className="w-8 h-8 bg-blue-600 rounded-full flex items-center justify-center text-white text-sm font-bold">2</div>
          <div className="text-xs text-slate-600 text-center">Schedule<br/>Games</div>
        </div>
        <div className="flex items-center pb-5"><ChevronRight className="w-4 h-4 text-slate-300" /></div>
        <div className="flex flex-col items-center gap-1">
          <div className="w-8 h-8 bg-blue-600 rounded-full flex items-center justify-center text-white text-sm font-bold">3</div>
          <div className="text-xs text-slate-600 text-center">Track<br/>Live</div>
        </div>
      </div>
    ),
  },
];

const WelcomeTourModal: React.FC<WelcomeTourModalProps> = ({ onComplete, onSkip }) => {
  const [current, setCurrent] = useState(0);
  const slide = slides[current];
  const isLast = current === slides.length - 1;
  const Icon = slide.icon;

  const handleNext = () => {
    if (isLast) {
      onComplete();
    } else {
      setCurrent(c => c + 1);
    }
  };

  const handlePrev = () => {
    setCurrent(c => Math.max(0, c - 1));
  };

  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm overflow-hidden animate-fade-in-up">
        {/* Header strip */}
        <div className={`${slide.color} px-6 pt-6 pb-8 relative`}>
          <button
            onClick={onSkip}
            className="absolute top-3 right-3 p-1.5 rounded-full bg-white/20 hover:bg-white/30 transition-colors text-white"
          >
            <X className="w-4 h-4" />
          </button>
          <div className="flex items-center gap-2 mb-1">
            <span className="text-xs font-semibold tracking-widest text-white/70 uppercase">SideoutIQ</span>
          </div>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center">
              <Icon className="w-5 h-5 text-white" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-white leading-tight">{slide.title}</h2>
              <p className="text-white/80 text-sm">{slide.subtitle}</p>
            </div>
          </div>
        </div>

        {/* Body */}
        <div className="px-6 pt-5 pb-4">
          <p className="text-slate-600 text-sm leading-relaxed mb-4">{slide.description}</p>

          {slide.visual && (
            <div className={`${slide.bgLight} rounded-xl p-4 flex items-center justify-center mb-4`}>
              {slide.visual}
            </div>
          )}

          {/* Dots */}
          <div className="flex items-center justify-center gap-1.5 mb-4">
            {slides.map((_, i) => (
              <button
                key={i}
                onClick={() => setCurrent(i)}
                className={`rounded-full transition-all ${i === current ? 'w-5 h-2 bg-blue-600' : 'w-2 h-2 bg-slate-200 hover:bg-slate-300'}`}
              />
            ))}
          </div>

          {/* Navigation */}
          <div className="flex gap-2">
            {current > 0 && (
              <button
                onClick={handlePrev}
                className="flex items-center gap-1 px-4 py-2.5 text-slate-500 hover:bg-slate-50 rounded-xl transition-colors text-sm font-medium"
              >
                <ChevronLeft className="w-4 h-4" /> Back
              </button>
            )}
            <button
              onClick={handleNext}
              className={`flex-1 flex items-center justify-center gap-1.5 py-2.5 rounded-xl font-bold text-sm transition-all active:scale-95 ${slide.color} text-white hover:opacity-90`}
            >
              {isLast ? 'Get Started' : 'Next'}
              {!isLast && <ChevronRight className="w-4 h-4" />}
            </button>
          </div>

          {!isLast && (
            <button
              onClick={onSkip}
              className="w-full text-center text-xs text-slate-400 hover:text-slate-600 mt-2 py-1 transition-colors"
            >
              Skip tour
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default WelcomeTourModal;
